import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CookieContext = createContext();

// Cookie categories
export const COOKIE_CATEGORIES = {
  necessary: 'necessary',
  functional: 'functional',
  analytics: 'analytics',
  marketing: 'marketing'
};

// Default consent state
const DEFAULT_CONSENT = {
  necessary: true, // Always required
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: null,
  version: '1.0'
};

const CONSENT_COOKIE_NAME = 'cookie_consent';
const CONSENT_EXPIRY_DAYS = 365;

export const CookieProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load consent from cookie on mount
  useEffect(() => {
    const savedConsent = getCookie(CONSENT_COOKIE_NAME);
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        setShowBanner(false);
        // Apply tracking based on saved consent
        applyConsent(parsed);
      } catch (e) {
        console.error('Failed to parse cookie consent:', e);
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
    setIsLoaded(true);
  }, []);

  // Save consent to cookie
  const saveConsent = useCallback((newConsent) => {
    const consentWithTimestamp = {
      ...newConsent,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consentWithTimestamp), CONSENT_EXPIRY_DAYS);
    setConsent(consentWithTimestamp);
    setShowBanner(false);
    setShowSettings(false);
    
    // Apply tracking scripts based on consent
    applyConsent(consentWithTimestamp);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
  }, [saveConsent]);

  // Reject non-essential cookies
  const rejectNonEssential = useCallback(() => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
  }, [saveConsent]);

  // Save custom preferences
  const savePreferences = useCallback((preferences) => {
    saveConsent({
      necessary: true, // Always true
      ...preferences
    });
  }, [saveConsent]);

  // Reset consent (for settings page)
  const resetConsent = useCallback(() => {
    deleteCookie(CONSENT_COOKIE_NAME);
    setConsent(DEFAULT_CONSENT);
    setShowBanner(true);
    
    // Remove tracking scripts
    removeTrackingScripts();
  }, []);

  // Open settings modal
  const openSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  // Close settings modal
  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Check if a specific category is consented
  const hasConsent = useCallback((category) => {
    return consent[category] === true;
  }, [consent]);

  return (
    <CookieContext.Provider value={{
      consent,
      showBanner,
      showSettings,
      isLoaded,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      resetConsent,
      openSettings,
      closeSettings,
      hasConsent,
      setShowBanner
    }}>
      {children}
    </CookieContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
};

// Helper functions
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
  }
  return null;
}

function deleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// Apply tracking based on consent
async function applyConsent(consent) {
  // Fetch marketing config from backend
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${API_URL}/api/marketing-config`);
    
    if (!response.ok) {
      console.warn('Could not fetch marketing config');
      return;
    }
    
    const config = await response.json();
    
    // Google Analytics
    if (consent.analytics && config.enabled?.google_analytics && config.google_analytics_id) {
      loadGoogleAnalytics(config.google_analytics_id);
    }
    
    // Facebook Pixel (for Facebook & Instagram)
    if (consent.marketing && config.enabled?.facebook_pixel && config.facebook_pixel_id) {
      loadFacebookPixel(config.facebook_pixel_id);
    }
    
    // Google Ads
    if (consent.marketing && config.enabled?.google_ads && config.google_ads_id) {
      loadGoogleAds(config.google_ads_id);
    }
  } catch (error) {
    console.error('Error loading tracking config:', error);
  }
}

// Load Google Analytics
function loadGoogleAnalytics(measurementId) {
  // Check if already loaded
  if (window.gtag) return;
  
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    console.warn('Google Analytics ID not configured');
    return;
  }
  
  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.id = 'google-analytics';
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId, {
    anonymize_ip: true // IP anonymization for GDPR
  });
  
  console.log('Google Analytics loaded with ID:', measurementId);
}

// Load Facebook Pixel (works for both Facebook and Instagram ads)
function loadFacebookPixel(pixelId) {
  // Check if already loaded
  if (window.fbq) return;
  
  if (!pixelId || pixelId === 'XXXXXXXXXXXXXXXX') {
    console.warn('Facebook Pixel ID not configured');
    return;
  }
  
  // Facebook Pixel base code
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;t.id='facebook-pixel';s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  
  console.log('Facebook Pixel loaded with ID:', pixelId);
}

// Load Google Ads
function loadGoogleAds(conversionId) {
  // Check if already loaded
  if (window.gtag && document.getElementById('google-ads')) return;
  
  if (!conversionId || conversionId === 'AW-XXXXXXXXXX') {
    console.warn('Google Ads Conversion ID not configured');
    return;
  }
  
  // If gtag not loaded, load it first
  if (!window.gtag) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
    script.id = 'google-ads';
    document.head.appendChild(script);
    
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
  }
  
  window.gtag('config', conversionId);
  
  console.log('Google Ads loaded with ID:', conversionId);
}

// Remove tracking scripts
function removeTrackingScripts() {
  // Remove Google Analytics
  const gaScript = document.getElementById('google-analytics');
  if (gaScript) gaScript.remove();
  delete window.gtag;
  delete window.dataLayer;
  
  // Remove Facebook Pixel
  const fbScript = document.getElementById('facebook-pixel');
  if (fbScript) fbScript.remove();
  delete window.fbq;
  delete window._fbq;
  
  // Clear tracking cookies
  const trackingCookies = ['_ga', '_gid', '_gat', '_fbp', '_fbc'];
  trackingCookies.forEach(cookie => deleteCookie(cookie));
  
  console.log('Tracking scripts removed');
}

export default CookieProvider;
